const api = process.env.REACT_APP_API_URL;

export const CGPG_GRID_ACTION_FIELDS = new Set(["reject", "approve", "edit"]);

export function getClickedGridCellField(event) {
	return event?.target?.closest?.("[data-field]")?.getAttribute?.("data-field") ?? null;
}

export function isCgpgGridActionField(field) {
	return field != null && CGPG_GRID_ACTION_FIELDS.has(field);
}

export function getCgpgDetailPanelGridSx(detailPanelInset, detailPanelWidth) {
	return {
		"& .MuiDataGrid-detailPanel": {
			marginLeft: `${detailPanelInset}px`,
			width: `${detailPanelWidth}px`,
			maxWidth: `${detailPanelWidth}px`,
			boxSizing: "border-box",
			py: 1.5,
		},
	};
}

export const urls = {
	userCountries: `${api}GetUserCountries?`,
	groupStatus: `${api}PGCGApproval/GetStatus`,
	periods: `${api}PGCGApproval/GetPeriods`,
	productGroups: `${api}ProductGroup/GetProductGroupsWithStatus`,
	customerGroups: `${api}CustomerGroup/GetCstmrGrpWithStatus`,
	rejectApproveProductGroup: `${api}ProductGroup/ApproveProductGroup`,
   rejectApproveCustomerGroup: `${api}CustomerGroup/ApproveCstmrGrp`,
   approveRejectMultiPG: `${api}ProductGroup/ApproveProductMultiple`,
   approveRejectMultiCG: `${api}CustomerGroup/ApproveCustomerMultiple`,
   pgHistory: `${api}ProductGroup/GetPGHistory?`,
   PGModificationDetails:`${api}ProductGroup/GetPGModificationDetails?`,
   cgHistory: `${api}CustomerGroup/GetCGHistory?`,
   CGModificationDetails:`${api}CustomerGroup/GetCGModificationDetails?`,
};

export function getPGHistoryRows(pgHistory) {
	const history = Array.isArray(pgHistory)
		? pgHistory
		: (pgHistory?.pgHistory ?? pgHistory?.PGHistory ?? []);
	return history.map((item, index) => {
		const row = {
			id: item.packGroupModificationID ?? item.PackGroupModificationID ?? `${item.submissionNumber ?? item.SubmissionNumber}-${index}`,
			submissionNumber: item.submissionNumber ?? item.SubmissionNumber,
			status: item.status ?? item.Status,
			comments: item.comments ?? item.Comments,
			modifiedDate: item.modifiedDate ?? item.ModifiedDate,
			modifiedBy: item.modifiedBy ?? item.ModifiedBy,
			packGroupModificationID: item.packGroupModificationID ?? item.PackGroupModificationID,
			packGroupID: item.packGroupID ?? item.PackGroupID,
		};

		return getApproveStatus(row);
	});
}

export function getCGHistoryRows(cgHistory) {
	const history = Array.isArray(cgHistory)
		? cgHistory
		: (cgHistory?.cgHistory ?? cgHistory?.CGHistory ?? []);
	return history.map((item, index) => {
		const row = {
			id: item.customerGroupModificationID ?? item.CustomerGroupModificationID ?? `${item.submissionNumber ?? item.SubmissionNumber}-${index}`,
			submissionNumber: item.submissionNumber ?? item.SubmissionNumber,
			status: item.status ?? item.Status,
			comments: item.comments ?? item.Comments,
			modifiedDate: item.modifiedDate ?? item.ModifiedDate,
			modifiedBy: item.modifiedBy ?? item.ModifiedBy,
			customerGroupModificationID: item.customerGroupModificationID ?? item.CustomerGroupModificationID,
			customerGroupID: item.customerGroupID ?? item.CustomerGroupID,
		};

		return getApproveStatus(row);
	});
}

function getSubmissionOrderValue(submissionNumber) {
	const match = String(submissionNumber ?? "").match(/(\d+)\s*$/);
	return match ? Number(match[1]) : Number.NaN;
}

function getHistoryRowModificationId(row) {
	return row.packGroupModificationID
		?? row.customerGroupModificationID
		?? row.id
		?? 0;
}

export function getLatestHistoryRowId(historyRows) {
	if (!Array.isArray(historyRows) || historyRows.length === 0) {
		return null;
	}

	let latestRow = historyRows[0];
	let latestOrder = getSubmissionOrderValue(latestRow.submissionNumber);

	for (const row of historyRows) {
		const order = getSubmissionOrderValue(row.submissionNumber);

		if (!Number.isNaN(order)) {
			if (Number.isNaN(latestOrder) || order > latestOrder) {
				latestOrder = order;
				latestRow = row;
			}
			continue;
		}

		if (Number.isNaN(latestOrder) && getHistoryRowModificationId(row) > getHistoryRowModificationId(latestRow)) {
			latestRow = row;
		}
	}

	return latestRow?.id ?? null;
}

export function getProductGroupDetailRecord(pG_Details) {
	if (!Array.isArray(pG_Details) || pG_Details.length === 0) {
		return null;
	}

	const detail = pG_Details[0];

	return {
		packGroupName: detail.packGroupName ?? detail.PackGroupName ?? "",
		effectiveStartDate: detail.effectiveStartDate ?? detail.EffectiveStartDate,
		effectiveEndDate: detail.effectiveEndDate ?? detail.EffectiveEndDate,
	};
}

export function getCustomerGroupDetailRecord(cG_Details) {
	if (!Array.isArray(cG_Details) || cG_Details.length === 0) {
		return null;
	}

	const detail = cG_Details[0];

	return {
		customerGroupName: detail.customerGroupName ?? detail.CustomerGroupName ?? "",
		effectiveStartDate: detail.effectiveStartDate ?? detail.EffectiveStartDate,
		effectiveEndDate: detail.effectiveEndDate ?? detail.EffectiveEndDate,
	};
}

export function getModificationSkuRows(pgModificationDetails) {
	const modificationDetails = normalizePgModificationDetails(pgModificationDetails);

	return modificationDetails.map((item, index) => ({
		id: item.skuID ?? item.SKUID ?? index,
		sku: item.sku ?? item.SKU ?? "",
		skuId: item.skuID ?? item.SKUID,
		skuNumber: item.skuNumber ?? item.SKUNumber ?? item.skunumber ?? "",
		isActive: item.isActive ?? item.IsActive,
	}));
}

export function normalizePgModificationDetails(pgModificationDetails) {
	if (Array.isArray(pgModificationDetails)) {
		return pgModificationDetails;
	}

	return [];
}

export function normalizeCgModificationDetails(cgModificationDetails) {
	if (Array.isArray(cgModificationDetails)) {
		return cgModificationDetails;
	}

	return [];
}

export function getModificationCustomerRows(cgModificationDetails) {
	const modificationDetails = normalizeCgModificationDetails(cgModificationDetails);

	return modificationDetails.map((item, index) => ({
		id: item.customerID ?? item.CustomerID ?? index,
		customer: item.customer ?? item.Customer ?? "",
		customerId: item.customerID ?? item.CustomerID,
		customerNumber: item.customerNumber ?? item.CustomerNumber ?? "",
		isActive: item.isActive ?? item.IsActive,
	}));
}

export const views = [
	{ key: 1, value: "Product Group" },
	{ key: 2, value: "Customer Group" },
];

export function getProductGroupsTable(data) {
	const rows = getProductGroupsRow(data);
	for(let i = 0; i < rows.length; i++) {
		rows[i] = getApproveStatus(rows[i]);
	}
	return { rows };
}

function getProductGroupsRow(data) {
	return data.map((item) => {
		return {
			id: item.productGroupID,
			productGroupName: item.productGroupName,
			countryCode: item.countryCode,
			status: item.status,
			updatedDate: item.updatedDate,
			updatedBy: item.updatedBy,
		};
	});
}

export function getCustomerGroupsTable(data) {
	const rows = getCustomerGroupsRow(data);
	for(let i = 0; i < rows.length; i++) {
		rows[i] = getApproveStatus(rows[i]);
	}
	return { rows };
}


function getCustomerGroupsRow(data) {
	return data.map((item) => {
		return {
			id: item.customerGroupID,
			customerGroupName: item.customerGroupName,
			countryCode: item.countryCode,
			status: item.status,
			updatedDate: item.updatedDate,
			updatedBy: item.updatedBy,
		};
	});
}


export function getApproveStatus (item){
	let groupStatus = item.status.toLowerCase();
	const logedUser = JSON.parse(localStorage.getItem("data"));
	if (logedUser.isAnalyst === true) {
		if (groupStatus === "submitted" || groupStatus === "locally approved" 
			|| groupStatus === "submitted – approved and modified" 
			|| groupStatus === 'locally approved – approved and modified'){
				item['canApproveReject'] = false;
				item['canEdit']=false;
			}
	}
	else{
		if (groupStatus === "approved" || groupStatus === "rejected"){
				item['canApproveReject'] = false;
				item['canEdit']=true;
		}
		else{
			if ((logedUser.isAdmin || (logedUser.isHQUser && logedUser.isApprover))){
				if (groupStatus === "submitted" || groupStatus === "locally approved" || groupStatus === "submitted – approved and modified" 
					|| groupStatus === 'locally approved – approved and modified'){
						item['canApproveReject'] = true;
						item['canEdit']=false;
					}
			}
			else if (!logedUser.isHQUser){
				if (groupStatus === "submitted" || groupStatus === "submitted – approved and modified"){
						item['canApproveReject'] = true;
						item['canEdit']=false;
				}
				else if(groupStatus === "locally approved"){
						item['canApproveReject'] = false;
						item['canEdit']=false;
				}
			}
		}
	}
	return item;
}
